import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { TableColumnWidthInfo } from '@devexpress/dx-react-grid';
import { Grid, ListItemIcon, ListItemText, Menu, MenuItem, PopoverPosition, Tab, Tabs } from '@mui/material';
import { format } from 'date-fns-tz';
import SummaryButton from '@/components/CampaignHeader/SummaryButton/SummaryButton';
import DialogSearch from '@/components/DialogSearch/DialogSearch';
import { buttonTriggerLateralFilter, findFilterKey } from '@/components/SearchTable/search-table-utils';
import Button from '@/components/UI/Button/Button';
import ConfirmDialog from '@/components/UI/Dialog/ConfirmDialog';
import FilterSelectionOverview from '@/components/UI/FilterSelectionOverview/FilterSelectionOverview';
import { IcoButtonSimple } from '@/components/UI/IcoButton/IcoButton';
import SimpleButton from '@/components/UI/SimpleButton/SimpleButton';
import Spinner from '@/components/UI/Spinner/Spinner';
import * as Const from '@/helpers/constants';
import { CR_PERMISSION_ACTIONS } from '@/helpers/crPermissions';
import { buildOpensearchQueryData, opensearchFieldMapping } from '@/helpers/opensearch-utils';
import { processSorting } from '@/helpers/tables-utils';
import { isCampaignInRD, isSubCRById, isVansBeforeFlowRemoval } from '@/helpers/utils';
import useApiRequest from '@/hooks/useApiRequest';
import useAppLanguage from '@/hooks/useAppLanguage';
import useAuth from '@/hooks/useAuth';
import useCRPermissionMatrix from '@/hooks/useCRPermissionMatrix';
import useDownloadFile from '@/hooks/useDownloadFile';
import useError from '@/hooks/useError';
import useGlobalSpinnerContext from '@/hooks/useGlobalSpinner';
import useGoTo from '@/hooks/useGoTo';
import useToastContext from '@/hooks/useToastContext';
import useVisibilityMatrix from '@/hooks/useVisibilityMatrix';
import { setCampaignStatusesForms } from '@/redux/Forms/forms.action';
import { getAffectedVehiclesForms, getCampaignStatusesForms, getClassificationForms, getDescriptionForms } from '@/redux/Forms/forms.reducer';
import { setOverviewFilters, setOverviewFiltersFocus } from '@/redux/OverviewFilters/overviewFilters.action';
import {
  getFilterOverview, getRowsPerPageCR, getSortOverview, getOrderColumnsOverview, getWidthColumnsOverview, getVisibilityColumnsOverview,
} from '@/redux/OverviewFilters/overviewFilters.reducer';
import { getSettingsInfo, getUserAll } from '@/redux/User/user.reducer';
import campaignServices from '@/services/campaignServices';
import titleService from '@/services/titleService';
import {
  CampaignInfoOverview, UserSearch, FiltersOverview, GlobalFilters, SortOverview, SubCampaignInfoOverview, VisibilityColumnsOverview,
  UserCompleteInfo, TableTranslations, SubCampaignInfo,
} from '@/types';
import { StatusCampaign, StatusSubCampaign, VisibilityMatrixAction, VisibilityMatrixFeature, WorkflowType } from '@/types/Matrix';
import useQuery from '@/utils/hooks';
import CampaignRequestsTable from './CampaignRequestsTable';
import styles from './CampaignRequestsOpenSearch.module.scss';

const { filterPills: fp } = Const;
export const filtersByRole = {
  [Const.CREATOR]: [fp.allRequests, fp.myOpenRequests, fp.myRequests, fp.representativeApplications, fp.deletedRequests],
  [Const.CREATOR_JV]: [fp.allRequests, fp.myOpenRequests, fp.myRequests, fp.representativeApplications, fp.deletedRequests],
  [Const.CREATOR_EXTERNAL]: [fp.allRequests, fp.myOpenRequests, fp.myRequests, fp.representativeApplications, fp.deletedRequests],
  [Const.COMMITTEE_COORDINATOR]: [fp.allRequests, fp.inPlantConsolidation, fp.coordinationToPlantApproval, fp.deletedRequests],
  [Const.RD_COMMITTEE_COORDINATOR]: [fp.allRequests, fp.deletedRequests, fp.notAssigned, fp.assignedToMe],
  [Const.APPROVER]: [fp.allRequests, fp.representativeApplications, fp.requestsToApprove, fp.deletedRequests],
  [Const.APPROVER_JV]: [fp.allRequests, fp.representativeApplications, fp.requestsToApprove, fp.deletedRequests],
  [Const.ADMIN]: [fp.allRequests, fp.myOpenRequests, fp.myRequests, fp.representativeApplications, fp.requestsToApprove, fp.deletedRequests,
    fp.notAssigned, fp.assignedToMe],
  [Const.VAN_COMMITTEE_COORDINATOR]: [fp.allRequests, fp.assignedToMe, fp.notAssigned, fp.deletedRequests, fp.closedRequests],
  [Const.VAN_E3_CAUSER]: [fp.allRequests, fp.requestsToApprove, fp.deletedRequests, fp.closedRequests],
  [Const.VAN_E3_CHIEF_ENGINEER_CARLINE]: [fp.allRequests, fp.requestsToApprove, fp.deletedRequests],
  [Const.VAN_E3_QMB_PLANT_APPROVER]: [fp.allRequests, fp.requestsToApproveSubCr, fp.deletedRequests],
  [Const.VAN_E2_QMB_APPROVER]: [fp.allRequests, fp.requestsToAcknowledge, fp.deletedRequests],
  [Const.VAN_CAMPAIGN_MANAGER]: [fp.allRequests, fp.notAssignedSubCr, fp.myOpenRequests, fp.deletedRequests, fp.assignedToMeSubCr],
  [Const.CAMPAIGN_MANAGER]: [fp.allRequests, fp.representativeApplications, fp.notAssignedSubCr, fp.assignedToMeSubCr, fp.inPlantApprovalSubCr],
  [Const.CAMPAIGN_MANAGER_JV]: [fp.allRequests, fp.representativeApplications, fp.notAssignedSubCr, fp.assignedToMeSubCr, fp.inPlantApprovalSubCr],
  [Const.CART_QMB_PLANT_APPROVER]: [fp.allRequests, fp.representativeApplications, fp.requestsToApproveSubCr],
  [Const.QMB_PLANT_APPROVER_JV]: [fp.allRequests, fp.representativeApplications, fp.requestsToApproveSubCr],
  [Const.CART_PLANT_SUPERVISOR]: [fp.allRequests, fp.representativeApplications],
  OTHER: [fp.allRequests],
};

const getFiltersForCurrentRole = (authService) => {
  const selectedRole = authService.getUserRole(true)[0];
  const baseFilters = filtersByRole?.[selectedRole] || filtersByRole.OTHER;

  if (selectedRole !== Const.VAN_E2_QMB_APPROVER) return baseFilters;

  const vanE3PlantApproverRole = `${Const.CART_ROLE_PREFIX}${Const.VAN_E3_QMB_PLANT_APPROVER}`;
  const hasVanE3PlantApproverRole = authService.getUserRoles().includes(vanE3PlantApproverRole);

  if (!hasVanE3PlantApproverRole) return baseFilters;

  return [fp.allRequests, fp.requestsToAcknowledge, fp.requestsToApproveSubCr, fp.deletedRequests];
};

const sortColumnExtension = [
  { columnName: Const.OVERVIEW_COLUMNS.ESTIMATED_CARS, sortingEnabled: false },
  { columnName: Const.OVERVIEW_COLUMNS.VALIDATED_CARS, sortingEnabled: false },
  { columnName: 'affectedPlants', sortingEnabled: false },
  { columnName: 'affectedModelSeries', sortingEnabled: false },
  { columnName: 'campaign', sortingEnabled: false },
  { columnName: Const.CLASSIFICATION.BLOCKING_RECOMENDATION, sortingEnabled: false },
  { columnName: 'locationInternal', sortingEnabled: false },
  { columnName: 'mbosRelevance', sortingEnabled: false },
  { columnName: Const.DESCRIPTION.SUPPLIER.NUMBER, sortingEnabled: false },
  { columnName: Const.DESCRIPTION.SUPPLIER.NAME, sortingEnabled: false },
  { columnName: Const.DESCRIPTION.CONTACT_PERSON_MP, sortingEnabled: false },
  { columnName: Const.DESCRIPTION.IRA_NUMBER, sortingEnabled: false },
  { columnName: 'partNumbers', sortingEnabled: false },
  { columnName: Const.OVERVIEW_COLUMNS.BLOCKING_LOCATIONS, sortingEnabled: false },
  { columnName: Const.OVERVIEW_COLUMNS.NO_REWORK, sortingEnabled: false },
  { columnName: Const.OVERVIEW_COLUMNS.REWORK_NOT_STARTED, sortingEnabled: false },
  { columnName: Const.OVERVIEW_COLUMNS.REWORK_OPEN, sortingEnabled: false },
  { columnName: Const.OVERVIEW_COLUMNS.REWORK_FINISHED, sortingEnabled: false },
  { columnName: Const.OVERVIEW_COLUMNS.OK_WITHOUT_REWORK, sortingEnabled: false },
  { columnName: Const.OVERVIEW_COLUMNS.REWORK_IN_FIELD, sortingEnabled: false },
];

const filterColumnExtensions = [
  { columnName: Const.OVERVIEW_COLUMNS.ESTIMATED_CARS, filteringEnabled: false },
  { columnName: Const.OVERVIEW_COLUMNS.VALIDATED_CARS, filteringEnabled: false },
  { columnName: Const.OVERVIEW_COLUMNS.BLOCKING_LOCATIONS, filteringEnabled: false },
  { columnName: Const.OVERVIEW_COLUMNS.NO_REWORK, filteringEnabled: false },
  { columnName: Const.OVERVIEW_COLUMNS.REWORK_NOT_STARTED, filteringEnabled: false },
  { columnName: Const.OVERVIEW_COLUMNS.REWORK_OPEN, filteringEnabled: false },
  { columnName: Const.OVERVIEW_COLUMNS.REWORK_FINISHED, filteringEnabled: false },
  { columnName: Const.OVERVIEW_COLUMNS.OK_WITHOUT_REWORK, filteringEnabled: false },
  { columnName: Const.OVERVIEW_COLUMNS.REWORK_IN_FIELD, filteringEnabled: false },
];

interface CampaignRequestsProps {
  setGlobalFilters: (data: GlobalFilters) => void;
  filterOverview: FiltersOverview;
  sortOverview: SortOverview[];
  userCompleteInfo: UserCompleteInfo;
  campaignStatuses: string[];
  setStatuses: (data: string[]) => void;
  timeZone: string;
  pageSizeSetting: number;
  orderColumns: string[];
  widthColumns: TableColumnWidthInfo[];
  visibilityColumns: VisibilityColumnsOverview[];
  setOverviewFiltersFocus: (field: any) => {
    type: string;
    payload: any;
  };
  gb18285Forms: any;
  descriptionforms: any;
  affectedForms: any;
}

const CampaignRequests: React.FC<CampaignRequestsProps> = (props) => {
  const { setGlobalFilters, filterOverview, sortOverview, userCompleteInfo, campaignStatuses, setStatuses, timeZone,
    pageSizeSetting, widthColumns, orderColumns, visibilityColumns, setOverviewFiltersFocus, gb18285Forms, descriptionforms,
    affectedForms } = props;

  const [campaignRequests, setCampaignRequests] = useState<CampaignInfoOverview[]>([]);
  const [subCampaignRequests, setSubCampaignRequests] = useState<SubCampaignInfoOverview[]>([]);
  const [filterBtns, setFilterBtns] = useState(filtersByRole.OTHER);
  const [filterSelected, setFilterSelected] = useState('');
  const [openConfirmDialogDeleteCampaign, setOpenConfirmDialogDeleteCampaign] = useState(false);
  const { t } = useTranslation();
  const lang = useAppLanguage();
  const authService = useAuth();
  const { addToast } = useToastContext();
  const { canSee } = useVisibilityMatrix();
  const [resetFilter, setResetFilter] = useState(0);
  const [resetData, setResetData] = useState<number>(0);
  const [copyRow, setCopyRow] = useState<number>(0);
  const [selectedOptions, setSelectedOptions] = useState<(CampaignInfoOverview | SubCampaignInfoOverview)[]>([]);
  const [canMakeDecissionByCC, setCanMakeDecissionByCC] = useState<boolean>(false);
  const [expandedRowIds, setExpandedRowIds] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [openAssignUserDialog, setOpenAssignUserDialog] = useState(false);
  const [openCampaignManagerDialog, setOpenCampaignManagerDialog] = useState(false);
  const [anchorRClickMenu, setAnchorRClickMenu] = useState<PopoverPosition | null>(null);
  const urlFilter = useQuery().get('filter') || fp.allRequests;
  const { setLoading } = useGlobalSpinnerContext();
  const { handleCriticalError } = useError();
  const { handleDocument } = useDownloadFile();
  const { goTo } = useGoTo();
  const firstSelectedCR: CampaignInfoOverview | SubCampaignInfoOverview = selectedOptions[0];
  const isVAN = (crOrSubCr: CampaignInfoOverview | SubCampaignInfoOverview) => isVansBeforeFlowRemoval(crOrSubCr?.type, crOrSubCr?.creationDate);
  const canReassign = selectedOptions.every((cr) => (isVAN(cr) ? cr?.assignedUser?.code === '' : true));
  const userCode = userCompleteInfo?.user?.userCode;
  const [assignManagerDisabled, setAssignManagerDisabled] = useState(true);
  const [tableTranslations, setTableTranslations] = useState<TableTranslations>();
  const checkDisabled = (status: string) => {
    if (status === Const.statusCampaign.IN_CREATION || status === Const.statusCampaign.DELETED) {
      return !authService?.checkRole(Const.CREATOR) && !authService?.checkRole(Const.ADMIN) && !authService?.checkRole(Const.CREATOR_JV);
    }

    return true;
  };
  const isMultiRoleUser = authService.checkMultiRoleUser();

  const hasSelection = selectedOptions.length > 0;

  const { isButtonDisabled, asStandIn } = useCRPermissionMatrix(selectedOptions[0]?.status, selectedOptions[0]?.authorization);
  const canSeeCR = (action: VisibilityMatrixAction) => selectedOptions.every((data) => canSee({
    feature: VisibilityMatrixFeature.CAMPAIGN_OVERVIEW,
    action: action,
    status: data?.status as StatusCampaign | StatusSubCampaign,
  }));
  const canSeeSubCR = (action: VisibilityMatrixAction) => canSee({
    feature: VisibilityMatrixFeature.SUB_CR,
    action: action,
  }) || !isButtonDisabled(action as unknown as CR_PERMISSION_ACTIONS);

  const deleteDisabled = () => {
    if (authService?.checkRoles([Const.CREATOR_EXTERNAL, Const.ADMIN]) || authService?.checkRoles([Const.CREATOR]) || (authService?.checkRoles([Const.CREATOR_JV])
      && selectedOptions[0].status === Const.statusCampaign.IN_CREATION)) {
      return false;
    }

    return selectedOptions.some((data) => data?.version > 1 || checkDisabled(data?.status)) || selectedOptions.length === 0;
  };
  const approveAllSubCRsByCC = canSee({
    feature: VisibilityMatrixFeature.CAMPAIGN_OVERVIEW,
    action: VisibilityMatrixAction.APPROVE_ALL_SUB_CRS_BY_CC,
    status: selectedOptions[0]?.status as StatusCampaign | StatusSubCampaign,
  }) && (selectedOptions[0] as CampaignInfoOverview)?.subCampaigns?.every((subCr) => canSee({
    feature: VisibilityMatrixFeature.CAMPAIGN_OVERVIEW,
    action: VisibilityMatrixAction.APPROVE_ALL_SUB_CRS_BY_CC,
    status: subCr.status as StatusCampaign | StatusSubCampaign,
  })) && selectedOptions.length === 1;

  const hasAtLeastOneSubCrRequiringDecision = (subCrs: SubCampaignInfo[]) => subCrs.some(
    (subCr) => subCr.status !== Const.statusSubCampaign.PLANT_NOT_AFFECTED,
  );

  const makeDecisionSubCRsByCC = canSee({
    feature: VisibilityMatrixFeature.CAMPAIGN_OVERVIEW,
    action: VisibilityMatrixAction.MAKE_DECISSION_SUB_CRS_BY_CC,
    status: selectedOptions[0]?.status as StatusCampaign | StatusSubCampaign,
  }) && (selectedOptions[0] as CampaignInfoOverview)?.subCampaigns?.every((subCr) => canSee({
    feature: VisibilityMatrixFeature.CAMPAIGN_OVERVIEW,
    action: VisibilityMatrixAction.MAKE_DECISSION_SUB_CRS_BY_CC,
    status: subCr.status as StatusCampaign | StatusSubCampaign,
  })) && selectedOptions.length === 1
  && hasAtLeastOneSubCrRequiringDecision((selectedOptions[0] as CampaignInfoOverview)?.subCampaigns);

  const committeCoordinatorCanApprove = async (id) => {
    try {
      const res = await campaignServices.canApproveSubCampaign(id);
      setCanMakeDecissionByCC(res);
    } catch (error) {
      addToast(error.message, Const.ERROR);

      return null;
    }
  };

  useEffect(() => {
    if (selectedOptions.length === 1 && authService.checkRoles([Const.COMMITTEE_COORDINATOR])) {
      committeCoordinatorCanApprove(selectedOptions[0]?.id);
    } else {
      setCanMakeDecissionByCC(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOptions]);

  const checkIsAssignable = useApiRequest(campaignServices.checkIsAssignable);

  useEffect(() => {
    async function enableOrDisableAssignManager() {
      if (selectedOptions.every((subCR) => canSee({
        feature: VisibilityMatrixFeature.SUB_CR,
        action: VisibilityMatrixAction.ASSIGN_MANAGER,
        workflow: (subCR as SubCampaignInfoOverview)?.workflowType as WorkflowType,
        status: subCR?.status as StatusCampaign | StatusSubCampaign,
      }))) {
        setLoading(true);

        // Verifica si los subCR son asignables
        const areAsignables = await Promise.all(
          selectedOptions.map(async (subCR) => {
            try {
              return await checkIsAssignable(subCR.id);
            } catch (error) {
              if (error.name !== 'AbortError') addToast(error.message, Const.ERROR);

              return false;
            }
          }),
        );

        // El botón estará deshabilitado solo si todos los CR tienen usuario asignado o no son asignables
        const allAssigned = selectedOptions.every(
          (subCR) => (isVAN(subCR) ? subCR.assignedUser?.code !== '' : false),
        );

        // Habilita el botón si al menos uno es asignable o no tiene usuario asignado
        setAssignManagerDisabled(allAssigned && !areAsignables.some((isAssignable) => isAssignable));

        setLoading(false);
      } else {
        setAssignManagerDisabled(true);
      }
    }

    enableOrDisableAssignManager();
  }, [selectedOptions]);

  useEffect(() => {
    if (campaignStatuses.length === 0) {
      Promise.all([
        campaignServices.getStatuses(false),
        campaignServices.getStatuses(true),
      ]).then((res) => {
        const campaignStatuses = res[0].statuses;
        const subCampaignStatuses = res[1].statuses;
        const allStatuses = [...campaignStatuses, ...subCampaignStatuses];
        setStatuses(allStatuses);
      })
        .catch(handleCriticalError);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    titleService.updatePageTitle(t('menu.campaignRequest'));
    localStorage.setItem(Const.STORAGE.LAST_OVERVIEW, Const.LAST_OVERVIEW_VALUES.CAMPAIGN_OVERVIEW);
  }, [t, lang]);

  useEffect(() => {
    setIsLoading(!pageSizeSetting);
  }, [pageSizeSetting]);

  useEffect(() => {
    const filters = getFiltersForCurrentRole(authService);
    const filter = filters.includes(urlFilter) ? urlFilter : fp.allRequests;

    setFilterBtns(filters);
    setFilterSelected(filter);

    if (filter !== urlFilter) {
      goTo(`${Const.ROUTES.CR_OVERVIEW}?filter=${fp.allRequests}`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authService, authService.roleSelected, authService.userRoles, urlFilter, lang, timeZone]);

  useEffect(() => {
    if (campaignRequests.length > 0) {
      setSelectedOptions(selectedOptions.filter((selectedItem) => campaignRequests.some((item) => item.id === selectedItem.id)));
    } else {
      setSelectedOptions([]); // Limpia la selección si no hay campañas
    }
  }, [campaignRequests]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setTableTranslations({
      ...tableTranslations,
      affectsChina: gb18285Forms?.affectsChina,
      classification: gb18285Forms?.classification,
      customerRelevancy: gb18285Forms?.customerRelevancy,
      source: descriptionforms?.source,
      blockingRecommendation: affectedForms?.blockingRecommendation,
    });
  }, [gb18285Forms, descriptionforms, affectedForms]); // eslint-disable-line react-hooks/exhaustive-deps

  const reloadOverview = () => {
    setCampaignRequests([]);
    setSubCampaignRequests([]);
    setSelectedOptions([]);
    setResetData((prev) => prev + 1);
  };

  const copyCampaign = (rows: CampaignInfoOverview[]) => {
    const campaign = rows.map((rowId) => campaignRequests.find((cr) => cr.id === rowId.id)?.id);
    setLoading(true);
    campaignServices.copyCampaignRequest(campaign)
      .then((crNumber) => {
        setTimeout(() => {
          addToast(t('alert.copyCRSuccessful', { crNumber }), Const.SUCCESS);
          setCampaignRequests([]);
          setResetData((prev) => prev + 1);
          setCopyRow((prev) => prev + 1);
          setLoading(false);
        }, 1500);
      })
      .catch((error) => {
        addToast(error.message, Const.ERROR);
        setLoading(false);
      });
  };

  const handleDeleteRowCampaignDelete = (rows: CampaignInfoOverview[]) => {
    const requestNumber = rows.map((rowId) => campaignRequests.find((cr) => cr.id === rowId.id)?.id);

    campaignServices.deleteCampaignInRecycleBin(requestNumber)
      .then(reloadOverview)
      .catch((error) => addToast(error.message, Const.ERROR));
  };

  const handleFilterClick = (filter: string) => {
    setGlobalFilters({ pillFilterOverview: JSON.stringify(filter) });
    goTo(`${Const.ROUTES.CR_OVERVIEW}?filter=${filter}`);
  };

  const deleteCampaingsToRecycleBin = useApiRequest(campaignServices.deleteCampaignsToRecycleBin);

  const onDialogConfimDeleteCampaign = () => {
    const campaign = selectedOptions.map((option) => option.id);

    if (filterSelected !== fp.deletedRequests) {
      setLoading(true);
      deleteCampaingsToRecycleBin(campaign)
        .then(() => {
          setTimeout(() => {
            reloadOverview();
            addToast(t('alert.removingSuccess'), Const.SUCCESS);
            setLoading(false);
          }, 1500);
        }).catch((error) => {
          addToast(error.message, Const.ERROR);
          setLoading(false);
        });
    } else {
      handleDeleteRowCampaignDelete(selectedOptions as CampaignInfoOverview[]);
    }
    setOpenConfirmDialogDeleteCampaign(false);
  };

  const getData = (model: string[]) => {
    const selection: (CampaignInfoOverview | SubCampaignInfoOverview)[] = model.map((rowId) => {
      if (isSubCRById(rowId)) {
        return subCampaignRequests?.find((campaign) => rowId === campaign.id);
      }

      return campaignRequests?.find((campaign) => rowId.includes(campaign.id));
    }).filter((item) => item !== undefined) ?? [];

    setSelectedOptions(selection);

    if (approveAllSubCRsByCC) {
      const canBeApproved: (CampaignInfoOverview | SubCampaignInfoOverview)[] = [];
      selection.map((subCR) => committeCoordinatorCanApprove(subCR.id).then((res) => {
        if (res !== null && res === true) {
          canBeApproved.push(subCR);
        }
      }));
      const newExpandedRows = canBeApproved.map((cr) => cr.id);
      const newAdded = newExpandedRows.filter((el) => !expandedRowIds.includes(el));
      if (newAdded.length && selection.length === 1) setExpandedRowIds(newExpandedRows);
    }
  };

  const assignCR = async (selectedUser: UserSearch[] | UserSearch | null) => {
    setLoading(true);
    setOpenAssignUserDialog(false);
    const user = selectedUser?.[0] || userCompleteInfo.user;
    const data = { code: user.userCode, department: user.department, summary: `${user.lastName}, ${user.firstName}, ${user.department}` };

    try {
      await Promise.all(selectedOptions.map(async (campaign) => {
        await campaignServices.putAssignCR(campaign.id, data, campaign.type, campaign.creationDate);
        campaign.assignedUser = data;
        await new Promise((resolve) => setTimeout(resolve, 500));
      }));
    } catch (error: any) {
      if (error.name !== 'AbortError') addToast(error.message, Const.ERROR);
    } finally {
      await new Promise((resolve) => setTimeout(resolve, 1500));
      setResetData(resetData + 1);
      setSelectedOptions([]); // Resetear la selección
    }
    setLoading(false);
  };

  const assignSubCR = async (manager: string | null) => {
    setLoading(true);
    const mngr = manager || userCode;

    try {
      await Promise.all(selectedOptions.map(async (subCR) => {
        await campaignServices.putAssignSubCR(subCR.id, mngr);
        subCR.assignedUser = { ...subCR.assignedUser, code: mngr as string };
        await new Promise((resolve) => setTimeout(resolve, 500));
      }));
    } catch (error: any) {
      if (error.name !== 'AbortError') addToast(error.message, Const.ERROR);
    } finally {
      await new Promise((resolve) => setTimeout(resolve, 1500));
      setResetData(resetData + 1);
      setSelectedOptions([]);
      setAssignManagerDisabled(true);
    }
    setLoading(false);
  };

  const sendToNextStatusAllSubCRs = () => {
    campaignServices.avanceAllSubCRsOfCRToNextStatus(firstSelectedCR.id)
      .then(() => {
        setCampaignRequests([]);
        setResetData(resetData + 1); // Forzar recarga de datos
      })
      .catch((error) => addToast(error.message, Const.ERROR));
  };

  const checkAssignToMeVisibility = canSeeCR(VisibilityMatrixAction.ASSIGN) && selectedOptions.length > 0 && canReassign;

  const checkAssignToVisibility = () => {
    if (selectedOptions.length === 0) return false;

    const isCreatorOfCrs = selectedOptions.every((cr) => cr.creationUser.code === userCode);
    const isInRD = selectedOptions.every((cr) => isCampaignInRD(cr.status));
    const isVANType = selectedOptions.every((cr) => isVAN(cr));
    const isVisible = isVANType ? (canSeeCR(VisibilityMatrixAction.ASSIGN_TO) && canReassign) : (canSeeCR(VisibilityMatrixAction.ASSIGN_TO) || (isInRD && isCreatorOfCrs));

    return isVisible;
  };

  const fetchCampaignsExcel = useApiRequest(campaignServices.getCROverviewResults);

  const downloadOverviewCRs = () => {
    setLoading(true);
    setAnchorRClickMenu(null);
    let filterTable: any = filterOverview?.map((f) => ({ columnName: f.columnName, value: f.value })) || [];
    filterTable = filterTable.reduce((acc, { columnName, value }) => ({ ...acc, [columnName]: value }), {});
    filterTable = buildOpensearchQueryData(filterTable);
    const filterKey = findFilterKey(sortOverview?.[0]?.columnName || opensearchFieldMapping.created, true);
    const columnsWithOrder = orderColumns.reduce((acc, column) => {
      const columnInfo = visibilityColumns.find((col) => col.columnName === column);

      if (columnInfo?.visible) acc.push(columnInfo?.columnName);

      return acc;
    }, [] as string[]);

    fetchCampaignsExcel(filterSelected, filterTable, processSorting(filterKey as string),
      sortOverview?.[0]?.direction?.toUpperCase() || Const.CAPITAL_ASC, columnsWithOrder, lang)
      .then((res) => handleDocument(`${format((new Date()), Const.DAY_DATE_FORMAT)}_Export_CaRT_Campaign_Overview.xlsx`, res?.blob))
      .catch((error) => {
        if (error.name !== Const.errorTypes.ABORT_ERROR) addToast(error.message, Const.ERROR);
      }).finally(() => setLoading(false));
  };

  const onRightClickMenu = (e: React.MouseEvent) => {
    e.preventDefault();
    setAnchorRClickMenu({ top: e.clientY - 2, left: e.clientX - 4 });
  };


  const activeFilters = filterOverview?.filter((element) => element?.value && (Array.isArray(element.value) ? element.value.length > 0 : element.value !== '')) ?? [];
  const badgeCount = activeFilters.length;
  const isInCheckingSubCR = selectedOptions[0]?.status === Const.statusSubCampaign.IN_CHECKING;

  const isCopyButtonDisabled = (selectedOptions: (CampaignInfoOverview | SubCampaignInfoOverview)[]): boolean => {
    if (selectedOptions.length !== 1) return true;

    const isOldVanCR = isVansBeforeFlowRemoval(selectedOptions[0]?.type, selectedOptions[0]?.creationDate);

    return isOldVanCR || checkDisabled(Const.statusCampaign.IN_CREATION);
  };

  return (
    <>
      <div className={`${styles.CampaignDetail} ${isMultiRoleUser ? styles.CampaignDetailMultiRole : ''}`}>
        {isLoading && <div className={styles.Spinner}><Spinner isVisible /></div>}
        {!isLoading && (
          <>
            <FilterSelectionOverview badgeCount={badgeCount} />
            <div className={styles.PageContent}>
              <div className={styles.ButtonsTable}>
                <Grid container columnSpacing={1} alignItems="center">
                  <Grid item xs={12} sm={12} md={12} lg={(hasSelection && canSeeSubCR(VisibilityMatrixAction.ASSIGN_MANAGER_OVERVIEW)) ? 6 : 7} xl={7}>
                    <div className={styles.ButtonsContentTableLeft}>
                      <Tabs variant="scrollable" value={filterSelected} scrollButtons="auto" TabIndicatorProps={{ style: { display: 'none' } }}>
                        {filterBtns.map((filterName) => (
                          <Tab
                            key={filterName}
                            value={filterName}
                            disableTouchRipple
                            disableRipple
                            className={styles.FilterTabChip}
                            label={(
                              <Button
                                key={`filter-chip-${filterName}`}
                                id={`filter-chip-${filterName}`}
                                handleClick={() => handleFilterClick(filterName)}
                                buttonStyle="GreyFilter"
                                btnText={t(`campaignRequests.${filterName}`)}
                                active={filterSelected === filterName}
                                extraButtonClasses={styles.FilterChip}
                              />
                            )}
                          />
                        ))}
                      </Tabs>
                    </div>
                  </Grid>
                  <Grid item xs={12} sm={12} md={12} lg={(hasSelection && canSeeSubCR(VisibilityMatrixAction.ASSIGN_MANAGER_OVERVIEW)) ? 6 : 5} xl={5}>
                    <div className={styles.ButtonsContentTableRight}>
                      {checkAssignToMeVisibility && (
                        <SimpleButton
                          id="btn-affectedtome-campaign-CR"
                          color="primary"
                          disabled={selectedOptions.length === 0 || selectedOptions.some((cr) => cr?.assignedUser?.code === userCode)}
                          buttonText={t('campaignRequests.assignMe')}
                          handleClick={() => assignCR(null)}
                          asStandIn={asStandIn}
                        />
                      )}
                      {checkAssignToVisibility() && (
                        <SimpleButton
                          id={(selectedOptions.every((cr) => isVAN(cr))) ? 'btn-assignto-campaign-CR-ATK' : 'btn-assignto-campaign-CR-RD'}
                          color="primary"
                          disabled={selectedOptions.length === 0}
                          buttonText={t(`campaignRequests.${(selectedOptions.every((cr) => isVAN(cr))) ? 'assignToATK' : 'rdCommittee'}`)}
                          handleClick={() => setOpenAssignUserDialog(true)}
                          asStandIn={asStandIn}
                        />
                      )}
                      {canSeeSubCR(VisibilityMatrixAction.ASSIGN_MANAGER_OVERVIEW) && (
                        <>
                          <SimpleButton
                            id="btn-affectedtome-campaign-subCR"
                            color="primary"
                            disabled={assignManagerDisabled || selectedOptions.some((subCR) => subCR?.assignedUser?.code === userCode)}
                            buttonText={t('campaignRequests.assignMe')}
                            handleClick={() => assignSubCR(null)}
                            asStandIn={asStandIn}
                          />
                          <SimpleButton
                            id="btn-affected-campaign-subCR"
                            color="primary"
                            disabled={assignManagerDisabled}
                            buttonText={t('campaignRequests.assignManager')}
                            handleClick={() => setOpenCampaignManagerDialog(true)}
                            asStandIn={asStandIn}
                          />
                        </>
                      )}
                      <IcoButtonSimple
                        id="btn-reset-sort"
                        iconComponent={<i className="di icon-filter-zuruecksetzen" />}
                        tooltipText={t('campaignRequests.resetFilter')}
                        handleClick={() => setResetFilter(resetFilter + 1)}
                        disabled={badgeCount === 0}
                      />
                      <IcoButtonSimple
                        id="btn-export-cr-overview"
                        iconComponent={<i className="di icon-datei-herunterladen" />}
                        tooltipText={t('campaignRequests.exportTable')}
                        handleClick={downloadOverviewCRs}
                      />
                      {(canSeeCR(VisibilityMatrixAction.DELETE) && selectedOptions.length > 0) && (
                        <IcoButtonSimple
                          disabled={deleteDisabled()}
                          id="btn-delete-campaignRequest"
                          icon="icon-muelleimer-loeschen"
                          tooltipText={t('campaignRequests.deleted')}
                          tooltipDisable={t('campaignRequests.cantDelete')}
                          handleClick={() => setOpenConfirmDialogDeleteCampaign(true)}
                        />
                      )}
                      {selectedOptions.length > 0 && (!isSubCRById(selectedOptions[0]?.id) || canSeeCR(VisibilityMatrixAction.COPY)) && (
                        <IcoButtonSimple
                          disabled={isCopyButtonDisabled(selectedOptions)}
                          id="btn-copy-campaignRequest"
                          icon="icon-datei-doppelt-kopieren"
                          tooltipText={t('campaignRequests.copy')}
                          tooltipDisable={t('campaignRequests.cantCopy')}
                          handleClick={() => copyCampaign(selectedOptions as CampaignInfoOverview[])}
                        />
                      )}
                      {(approveAllSubCRsByCC) && (
                        <SimpleButton
                          id="btn-approveAll-campaign-subCR-CC"
                          color="primary"
                          disabled={!canMakeDecissionByCC}
                          buttonText={t('campaignRequests.approveAll')}
                          handleClick={sendToNextStatusAllSubCRs}
                        />
                      )}
                      {(makeDecisionSubCRsByCC) && (
                        <SummaryButton
                          disabled={!canMakeDecissionByCC}
                          campaignInfoOverview={selectedOptions[0] as CampaignInfoOverview}
                          campaignId={selectedOptions[0]?.id}
                          campaignStatus={selectedOptions[0]?.status}
                          campaignVersion={selectedOptions[0]?.version}
                          isVANCR={isVAN(selectedOptions[0])}
                          isOverviewMakeDecisionByCC={selectedOptions.length > 0
                            ? (selectedOptions[0] as CampaignInfoOverview)?.subCampaigns?.find(
                              (subCr) => subCr.status !== Const.statusSubCampaign.PLANT_NOT_AFFECTED,
                            )?.status
                            : ''}
                          reloadOverview={reloadOverview}
                        />
                      )}
                    </div>
                  </Grid>
                </Grid>
              </div>
              <div className={styles.TableCampaigns}>
                <CampaignRequestsTable
                  isCampaignOverview
                  chipFilter={filterSelected}
                  setSelectedOption={getData}
                  selectedOption={selectedOptions}
                  timeZone={timeZone}
                  setCampaignRequests={(campaignRequestss: CampaignInfoOverview[]) => {
                    setCampaignRequests((previousState) => [...previousState, ...campaignRequestss]);
                  }}
                  setSubCampaignRequests={(subCampaignRequestss: SubCampaignInfoOverview[]) => {
                    setSubCampaignRequests((previousState) => [...previousState, ...subCampaignRequestss]);
                  }}
                  subCampaignRequests={subCampaignRequests}
                  columSorting={[{ columnName: 'created', direction: Const.DESC_ORDER }]}
                  setFilters={setGlobalFilters}
                  resetFilter={resetFilter}
                  resetData={resetData}
                  copyRow={copyRow}
                  filterOverview={filterOverview}
                  sortOverview={sortOverview}
                  campaignStatuses={campaignStatuses}
                  savedPageSize={pageSizeSetting}
                  onRightClickHeaderRow={onRightClickMenu}
                  expandedRows={expandedRowIds}
                  setExpandedRows={(expandedRows: any[]) => setExpandedRowIds(expandedRows)}
                  comitteeCoordinatorBehavior={approveAllSubCRsByCC}
                  treeDataConfig={{ childrenProperty: 'childRows', columnExpanded: 'title' }}
                  widthColumns={widthColumns}
                  orderColumns={orderColumns}
                  visibilityColumns={visibilityColumns}
                  userCompleteInfo={userCompleteInfo}
                  filterColumnExtensions={filterColumnExtensions}
                  sortColumnExtension={sortColumnExtension}
                  defaultMergeProperty="mainCR"
                  setOverviewFiltersFocus={setOverviewFiltersFocus}
                  tableTranslations={tableTranslations}
                  buttonTriggerLateralFilter={buttonTriggerLateralFilter}
                />
              </div>
            </div>
          </>
        )}
      </div>
      <Menu
        keepMounted
        open={anchorRClickMenu !== null}
        onClose={() => setAnchorRClickMenu(null)}
        anchorReference="anchorPosition"
        anchorPosition={anchorRClickMenu || undefined}
      >
        <MenuItem id="btn-export-cr-overview-table" onClick={downloadOverviewCRs}>
          <ListItemIcon>
            <i className="di icon-datei-herunterladen" />
          </ListItemIcon>
          <ListItemText primary={t('campaignRequests.exportTable')} />
        </MenuItem>
      </Menu>
      {openConfirmDialogDeleteCampaign && (
        <ConfirmDialog
          close={() => setOpenConfirmDialogDeleteCampaign(false)}
          confirm={onDialogConfimDeleteCampaign}
          reject={() => setOpenConfirmDialogDeleteCampaign(false)}
          title={t('dialog.deleteCampaign.title')}
          message={t('dialog.deleteCampaign.message')}
          closeBtnText={t('dialog.no')}
          confirmBtnText={t('dialog.yes')}
        />
      )}
      {openAssignUserDialog && (
        <DialogSearch
          role={(isVAN(firstSelectedCR) ? Const.VAN_COMMITTEE_COORDINATOR : Const.RD_COMMITTEE_COORDINATOR).toUpperCase()}
          title={t(`campaignRequests.${isVAN(firstSelectedCR) ? 'assignToATK' : 'rdCommittee'}`)}
          close={() => setOpenAssignUserDialog(false)}
          updateOptionSelected={assignCR}
          isRemote={false}
        />
      )}
      {openCampaignManagerDialog && (
        <DialogSearch
          plant={(firstSelectedCR as SubCampaignInfoOverview)?.plant}
          title={t('campaignRequests.assignManager')}
          close={() => setOpenCampaignManagerDialog(false)}
          updateOptionSelected={(managerSelected: string | null) => assignSubCR(managerSelected)}
          isRemote={false}
          assignedUser={isInCheckingSubCR ? firstSelectedCR?.assignedUser.code : undefined}
        />
      )}

    </>
  );
};

const mapStateToProps = (state) => ({
  userCompleteInfo: getUserAll(state),
  timeZone: getSettingsInfo(state).timezone,
  filterOverview: getFilterOverview(state),
  sortOverview: getSortOverview(state),
  campaignStatuses: getCampaignStatusesForms(state),
  pageSizeSetting: getRowsPerPageCR(state),
  widthColumns: getWidthColumnsOverview(state),
  orderColumns: getOrderColumnsOverview(state),
  visibilityColumns: getVisibilityColumnsOverview(state),
  gb18285Forms: getClassificationForms(state),
  descriptionforms: getDescriptionForms(state),
  affectedForms: getAffectedVehiclesForms(state),
});

const actionCreators = {
  setGlobalFilters: setOverviewFilters,
  setStatuses: setCampaignStatusesForms,
  setOverviewFiltersFocus: setOverviewFiltersFocus,
};

export default connect(mapStateToProps, actionCreators)(CampaignRequests);
